import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import Categories from '../components/Categories';
import ProductGrid from '../components/ProductGrid';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import LoadingSkeleton from '../components/LoadingSkeleton';
import MarketingPopup from '../components/MarketingPopup';
import WhyChooseUs from '../components/WhyChooseUs';
import BestSellers from '../components/BestSellers';
import BrandStory from '../components/BrandStory';
import FAQ from '../components/FAQ';
import Newsletter from '../components/Newsletter';
import ComingSoonMobileApp from '../components/ComingSoonMobileApp';
import QuickOrderModal from '../components/QuickOrderModal';
import { getProducts, Product } from '../api/products';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';

const Home: React.FC = () => {
    const { t } = useTranslation();
    const { addToCart } = useCart();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const data = await getProducts();
                setProducts(data);
            } catch (error) {
                console.error('Failed to fetch products', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    const openQuickOrder = (product: Product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    return (
        <div className="overflow-hidden">
            {/* Hero Section */}
            <Hero />

            {/* Categories Section */}
            <Categories />

            {/* Best Sellers Section */}
            {!loading && products.length > 0 && (
                <BestSellers products={products} onQuickOrder={openQuickOrder} />
            )}

            {/* Latest Products Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <motion.span
                            className="text-primary font-semibold tracking-wider uppercase text-sm"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            {t('home.collection', 'Our Collection')}
                        </motion.span>
                        <motion.h2
                            className="text-3xl md:text-4xl font-bold text-dark mt-2 mb-4"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                        >
                            {t('home.productsTitle')}
                        </motion.h2>
                        <motion.p
                            className="text-lg text-gray-600 max-w-2xl mx-auto"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                        >
                            {t('home.productsSubtitle')}
                        </motion.p>
                    </div>

                    {loading ? (
                        <LoadingSkeleton count={10} />
                    ) : (
                        <ProductGrid
                            products={products.slice(0, 10)}
                            loading={loading}
                            onQuickOrder={openQuickOrder}
                            enableAutoZoom={true}
                        />
                    )}

                    {/* View All Products Button */}
                    <div className="mt-12 text-center">
                        <Link
                            to="/category/all"
                            className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-white rounded-full font-bold hover:bg-primary-dark transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                        >
                            <span>{t('common.viewAll')}</span>
                            <svg className="w-5 h-5 rtl:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                    </div>

                </div>
            </section>

            {/* Why Choose Us Section */}
            <WhyChooseUs />

            {/* Brand Story Section */}
            <BrandStory />

            {/* Testimonials Section */}
            <TestimonialsCarousel />

            {/* FAQ Section */}
            <FAQ />

            {/* Newsletter Section */}
            <Newsletter />

            {/* Mobile App Coming Soon Section */}
            <ComingSoonMobileApp />

            {/* Marketing Popup */}
            <MarketingPopup />

            {/* Quick Order Modal */}
            <QuickOrderModal
                isOpen={isModalOpen}
                closeModal={() => setIsModalOpen(false)}
                product={selectedProduct}
            />
        </div>
    );
};

export default Home;
